import { UserStatus } from '@bp/bp-graphql-types';
import { ArrowHeadRightIcon, CloseIcon, Modal, ModalBottomButtons } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { backendApi } from 'utils/backendApi';
import { naturalStringSort } from 'utils/stringHelper';
import { showSuccessToast, showUserDbErrorToast } from 'utils/toasts';
import styles from './PersonsBulkInviteModal.module.scss';

const PersonsBulkInviteModalFragment = graphql(`
  fragment PersonsBulkInviteModalFields on Query {
    bulkInviteProfiles: profiles(where: { organization: { uuid: $organizationUuid } }) {
      __typename
      uuid
      email
      displayName
      user {
        __typename
        uuid
        status
      }
    }
  }
`);

type PersonSelection = {
  uuid: string;
  name: string;
  email: string | null;
  status: UserStatus;
};

type PersonsBulkInviteModalProps = {
  isOpen: boolean;
  selectedUuids: string[];
  onClose: () => void;
  data: FragmentType<typeof PersonsBulkInviteModalFragment> | undefined;
};

export const PersonsBulkInviteModal = ({ isOpen, selectedUuids, onClose, data }: PersonsBulkInviteModalProps) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(false);

  const profilesToInvite: PersonSelection[] = [];
  const alreadyInvited: PersonSelection[] = [];
  const noEmail: PersonSelection[] = [];
  const hasProfile: PersonSelection[] = [];
  const mailNotUnique: PersonSelection[] = [];

  const fragment = getFragmentData(PersonsBulkInviteModalFragment, data);
  const profiles = fragment?.bulkInviteProfiles ?? [];

  const selection: PersonSelection[] = profiles
    .filter((p) => selectedUuids.includes(p.uuid))
    .map((p) => ({
      uuid: p.uuid,
      name: p.displayName ?? '',
      email: p.email ?? null,
      status: p.user?.status ?? UserStatus.None,
    }));

  const allMails: { uuid: string; email: string }[] = profiles
    .filter((p) => !!p.email && p.email !== '')
    .map((p) => ({
      uuid: p.uuid,
      email: p.email!,
    }));

  selection.forEach((profile) => {
    if (!profile.email || profile.email === '') {
      noEmail.push(profile);
    } else if (profile.status === UserStatus.Full || profile.status === UserStatus.ImpersonateOnly) {
      hasProfile.push(profile);
    } else if (
      allMails
        .filter((p) => p.uuid !== profile.uuid)
        .map((p) => p.email)
        .includes(profile.email)
    ) {
      mailNotUnique.push(profile);
    } else if (profile.status === UserStatus.Invited) {
      alreadyInvited.push(profile);
      profilesToInvite.push(profile);
    } else {
      profilesToInvite.push(profile);
    }
  });

  const hasResend = alreadyInvited.length > 0;
  const hasIgnored = noEmail.length > 0 || hasProfile.length > 0 || mailNotUnique.length > 0;

  async function handleSubmit() {
    setLoading(true);

    if (profilesToInvite.length !== 0) {
      const { ok } = await backendApi.post(
        `/invite/`,
        JSON.stringify({
          profiles: profilesToInvite.map((p) => p.uuid),
          baseUrl: import.meta.env.VITE_APP_REDIRECT_URI,
        }),
      );
      if (!ok) {
        showUserDbErrorToast();
      } else {
        showSuccessToast(t('profiles.invite.invitationSent'));
      }
    }

    setLoading(false);
    onClose();
  }

  return (
    <Modal
      title={t('profiles.invite.sendInvitation')}
      isOpen={isOpen}
      width='s'
      loading={loading}
      onRequestClose={() => onClose()}
      className={styles['persons-bulk-invite-modal']}
    >
      <div className={styles.content}>
        <div className={styles.invite}>
          {profilesToInvite.length === 0 ? (
            <>
              <CloseIcon />
              {t('profiles.invite.noProfiles')}
            </>
          ) : (
            <>
              <ArrowHeadRightIcon />
              {t('profiles.invite.bulkInviteCount', { count: profilesToInvite.length })}
            </>
          )}
        </div>

        {/* Einladung erneut senden */}
        {hasResend && (
          <div className={styles.resend}>
            <div className={styles.section}>
              <div className={styles.headline}>{t('profiles.invite.alreadyInvited')}</div>
              {alreadyInvited
                .sort((a, b) => naturalStringSort(a.name, b.name))
                .map((p) => (
                  <div key={p.uuid} className={styles.entry}>
                    {p.name}
                  </div>
                ))}
            </div>
          </div>
        )}

        {hasIgnored && (
          <div className={styles['no-invite']}>
            <div className={styles.message}>{t('profiles.invite.noInvite')}:</div>

            {/* keine Email */}
            {noEmail.length > 0 && (
              <div className={styles.section}>
                <div className={styles.headline}>{t('profiles.invite.noEmail')}</div>
                {noEmail
                  .sort((a, b) => naturalStringSort(a.name, b.name))
                  .map((p) => (
                    <div key={p.uuid} className={styles.entry}>
                      {p.name}
                    </div>
                  ))}
              </div>
            )}

            {/* Profil bereits vorhanden */}
            {hasProfile.length > 0 && (
              <div className={styles.section}>
                <div className={styles.headline}>{t('profiles.invite.hasProfile')}</div>
                {hasProfile
                  .sort((a, b) => naturalStringSort(a.name, b.name))
                  .map((p) => (
                    <div key={p.uuid} className={styles.entry}>
                      {p.name}
                    </div>
                  ))}
              </div>
            )}

            {/* Mail doppelt */}
            {mailNotUnique.length > 0 && (
              <div className={styles.section}>
                <div className={styles.headline}>{t('profiles.invite.mailNotUnique')}</div>
                {mailNotUnique
                  .sort((a, b) => naturalStringSort(a.name, b.name))
                  .map((p) => (
                    <div key={p.uuid} className={styles.entry}>
                      {p.name} ({p.email})
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>

      <ModalBottomButtons
        closeButton={{ callback: onClose }}
        submitButton={{ callback: handleSubmit, disabled: profilesToInvite.length === 0 }}
      />
    </Modal>
  );
};
